import { Component } from "@angular/core";
import { NotifierService } from "src/app/_services/notifier.service";
import { APIService } from "src/app/api/api.service";
import { AzureCostOptimizationBaseComponent } from "../azure-costoptimization-base.component";
 
@Component({
  selector: "app-azure-vm-cost-optimization",
  templateUrl: "./azure-vm-costoptimization.component.html",
  styleUrls: [
    "../azure-costoptimization-base.component.css",
    "./azure-vm-costoptimization.component.css",
  ],
})
export class AzureVmCostOptimizationComponent extends AzureCostOptimizationBaseComponent {
  pageHeading: string = "VM Recommendations";
  recommendationSummaryByTab: any = {
    utilization:
      "Recommendations are based on underutilization analysis for the selected Azure resource group.",
    processor:
      "This VM is currently using an Intel-based processor. Consider migrating to an AMD-based VM or an Arm-based VM for potential cost and performance optimization.",
    os:
      "The current virtual machine uses the Windows operating system. Based on operating system optimization rules, we recommend evaluating a move to a Linux-based VM where feasible.",
  };
  protected scanAction: string = "vm";

  headers: any = [];
  currentPage: string = "utilization";
  currentPagination: any = { page: 1, perPage: 10 };

  recommendations: any = {
    utilization: [],
    processor: [],
    os: [],
  };

  tabLabels: any = {
    utilization: "Utilization",
    processor: "Processor",
    os: "OS",
  };

  utilizationHeaders: any[] = [
    { id: "vm_name", name: "Resource Name", filter: true },
    { id: "avg_cpu", name: "Avg CPU (%)" },
    { id: "max_cpu", name: "Max CPU (%)" },
    { id: "current_cost", name: "Current Cost (USD)" },
    { id: "recommended_cost", name: "Recommended Cost (USD)" },
    { id: "Savings", name: "Savings (USD)" },
    { id: "Reason", name: "Reason", filter: true },
    { id: "recommendation", name: "Recommendation", filter: true },
  ];

  processorHeaders: any[] = [
    { id: "vm_name", name: "Resource Name", filter: true },
    { id: "current_sku", name: "Current SKU", filter: true },
    { id: "target_sku", name: "Target SKU", filter: true },
    { id: "current_cost", name: "Current Cost (USD)" },
    { id: "recommended_cost", name: "Recommended Cost (USD)" },
    { id: "Savings", name: "Savings (USD)" },
    { id: "Reason", name: "Reason", filter: true },
    { id: "recommendation", name: "Recommendation", filter: true },
  ];

  osHeaders: any[] = [
    { id: "vm_name", name: "Resource Name", filter: true },
    { id: "current_os", name: "Current OS", filter: true },
    { id: "target_os", name: "Recommended OS", filter: true },
    { id: "current_cost", name: "Current Cost (USD)" },
    { id: "recommended_cost", name: "Recommended Cost (USD)" },
    { id: "Savings", name: "Savings (USD)" },
    { id: "Reason", name: "Reason", filter: true },
    { id: "recommendation", name: "Recommendation", filter: true },
  ];

  paginationByTab: any = {
    utilization: { page: 1, perPage: 10 },
    processor: { page: 1, perPage: 10 },
    os: { page: 1, perPage: 10 },
  };

  constructor(apiService: APIService, notifier: NotifierService) {
    super(apiService, notifier);
    this.currentPagination = this.paginationByTab[this.currentPage];
  }

  refresh(showGlobalLoader: boolean = false) {
    this.currentPage = "utilization";
    this.resetRecommendations();
    this.resetPaginations();
    this.currentPagination = this.paginationByTab[this.currentPage];
    super.refresh(showGlobalLoader);
  }

  setCurrentPage(tab: string) {
    this.currentPage = tab;
    this.currentPagination = this.paginationByTab[tab] || this.paginationByTab.utilization;
  }

  get recommendationSummary(): string {
    return (
      this.recommendationSummaryByTab[this.currentPage] ||
      this.recommendationSummaryByTab.utilization
    );
  }

  getCurrentRows(): any[] {
    return this.recommendations[this.currentPage] || [];
  }

  getCurrentHeaders(): any[] {
    if (this.currentPage == "processor") {
      return this.processorHeaders;
    }
    if (this.currentPage == "os") {
      return this.osHeaders;
    }
    return this.utilizationHeaders;
  }

  getCurrentTabLabel(): string {
    return this.tabLabels[this.currentPage] || "Recommendations";
  }

  getTotalSavings(tab: string = this.currentPage): string {
    const rows: any[] = this.recommendations[tab] || [];
    const total = rows.reduce((sum: number, row: any) => {
      const savingsValue =
        row.Savings ??
        row.estimated_monthly_savings_usd ??
        row.estimated_savings_usd ??
        0;
      return sum + Number(savingsValue || 0);
    }, 0);
    return total.toFixed(2);
  }

  async loadScanDetails(scanId: string) {
    if (!scanId) {
      this.fetching = false;
      return;
    }

    const detailsUrl = `${this.azureCustomAnalysisBasePath}/scan-details?${this.buildQuery(
      {
        user_id: this.userId,
        scan_id: scanId,
        subscription_id: this.accountId,
        action: this.scanAction,
      }
    )}`;

    let result: any = await this.apiService.getDataPromis(
      detailsUrl,
      {},
      this.authHeader
    );

    if (this.destroyed) {
      return;
    }

    if (
      result &&
      (result.status == "Completed" ||
        result.items ||
        result.underutilized_vms ||
        result.processor_vms ||
        result.os_vms)
    ) {
      const utilizationRows = this.getArrayData(
        result.underutilized_vms,
        result.items
      ).map((item: any) => this.normalizeUtilizationItem(item));
      const processorRows = this.getArrayData(result.processor_vms).map(
        (item: any) => this.normalizeProcessorItem(item)
      );
      const osRows = this.getArrayData(result.os_vms).map((item: any) =>
        this.normalizeOsItem(item)
      );

      this.recommendations = {
        utilization: utilizationRows,
        processor: processorRows,
        os: osRows,
      };

      // Keep base class total-savings compatibility.
      this.vmOptimizations = utilizationRows;

      this.jobDetails = this.normalizeScanItem({
        ...(this.jobDetails || {}),
        scan_id: result.scan_id || this.jobDetails?.scan_id,
        status: result.status || this.jobDetails?.status,
        scanned_date_time:
          result.scanned_date_time || this.jobDetails?.scanned_date_time,
        completed_aate_time:
          result.completed_aate_time || this.jobDetails?.completed_aate_time,
      });

      this.fetching = false;
    } else {
      this.resetRecommendations();
      const err = this.parseApiError(result);
      if (err.toLowerCase().indexOf("no record") == -1) {
        this.notifier.alert("Error", "", err, "error", 5000);
      }
      this.fetching = false;
    }
  }

  private resetRecommendations() {
    this.recommendations = {
      utilization: [],
      processor: [],
      os: [],
    };
    this.vmOptimizations = [];
  }

  private resetPaginations() {
    this.paginationByTab = {
      utilization: { page: 1, perPage: this.paginationByTab.utilization.perPage || 10 },
      processor: { page: 1, perPage: this.paginationByTab.processor.perPage || 10 },
      os: { page: 1, perPage: this.paginationByTab.os.perPage || 10 },
    };
  }

  private getArrayData(primary: any, fallback: any[] = []): any[] {
    if (Array.isArray(primary)) {
      return primary;
    }
    if (Array.isArray(fallback)) {
      return fallback;
    }
    return [];
  }

  private normalizeUtilizationItem(item: any): any {
    return {
      ...item,
      vm_name:
        item.vm_name || item.resource_name || item.resource_name_status,
      avg_cpu: item.avg_cpu ?? item.avg_cpu_percent,
      max_cpu: item.max_cpu ?? item.max_cpu_percent,
      current_cost: item.current_cost ?? item.current_monthly_cost_usd,
      recommended_cost:
        item.recommended_cost ||
        item.projected_monthly_cost_after_downgrade_usd,
      Savings: item.Savings ?? item.estimated_monthly_savings_usd,
      Reason: item.Reason || item.reason,
    };
  }

  private normalizeProcessorItem(item: any): any {
    return {
      ...item,
      vm_name:
        item.vm_name ||
        item.resource_name ||
        item.instancename ||
        item.resource_name_status,
      current_sku:
        item.current_sku ||
        item.current_instance_type ||
        item.instancetype ||
        item.instance_type,
      target_sku:
        item.target_sku ||
        item.recommended_sku ||
        item.recommendedInsType ||
        item.recommended_instance_type,
      current_cost:
        item.current_cost ||
        item.current_monthly_cost_usd ||
        item.currentPrice,
      recommended_cost:
        item.recommended_cost ||
        item.projected_monthly_cost_after_downgrade_usd ||
        item.recommendedPrice,
      Savings:
        item.Savings ||
        item.estimated_monthly_savings_usd ||
        (Number(item.currentPrice || 0) - Number(item.recommendedPrice || 0)).toFixed(2),
      Reason: item.Reason || item.reason,
    };
  }

  private normalizeOsItem(item: any): any {
    return {
      ...item,
      vm_name:
        item.vm_name ||
        item.resource_name ||
        item.instancename ||
        item.resource_name_status,
      current_os: item.current_os || item.platform || item.current_platform,
      target_os:
        item.target_os || item.recommended_os || item.recommendedOs,
      current_cost:
        item.current_cost ||
        item.current_monthly_cost_usd ||
        item.currentPrice,
      recommended_cost:
        item.recommended_cost ||
        item.projected_monthly_cost_after_downgrade_usd ||
        item.recommendedPrice,
      Savings:
        item.Savings ||
        item.estimated_monthly_savings_usd ||
        (Number(item.currentPrice || 0) - Number(item.recommendedPrice || 0)).toFixed(2),
      Reason: item.Reason || item.reason,
    };
  }
}
